import * as React from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'charjer.theme';

interface ThemeContextValue {
  /** What the user chose, including "follow the OS". */
  preference: ThemePreference;
  /** What is actually rendered right now. */
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Convenience for the account screen's toggle. */
  toggle: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(readStoredPreference);
  const [systemValue, setSystemValue] = React.useState<ResolvedTheme>(systemTheme);

  // Track the OS setting so "system" stays live rather than being read once.
  React.useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) =>
      setSystemValue(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: ResolvedTheme = preference === 'system' ? systemValue : preference;

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setPreference = React.useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      // The inline boot script in index.html reads this key; storing the
      // resolved value for "system" would freeze the OS preference, so the
      // raw choice is stored and "system" simply falls back at boot.
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* non-persistent preference is still applied for this session */
    }
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      setPreference,
      toggle: () => setPreference(resolvedTheme === 'dark' ? 'light' : 'dark'),
    }),
    [preference, resolvedTheme, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const context = React.use(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a <ThemeProvider>');
  return context;
}
